import { useEffect, useState, useRef, useMemo } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, Download, ArrowUpAZ, ArrowDownZA, Search, UserPlus } from "lucide-react";
import { PasswordConfirm } from "@/components/PasswordConfirm";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/roles";

// Deriva o username a partir do nome do entregador conforme o estilo escolhido
const deriveUsername = (nome, style) => {
  const first = (nome || "").trim().split(/\s+/)[0] || "";
  if (!first) return "";
  if (style === "upper") return first.toUpperCase();
  if (style === "cap") return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  return first.toLowerCase(); // default "lower"
};

export default function CrudPage({ title, path, extraFields = [], relations = {}, allowImport = false, enableUserAccount = false }) {
  const { user, currentEmpresaId } = useAuth();
  const resource = path.replace(/-/g, "_"); // "tipos-entrega" → "tipos_entrega" (chave da matrix)
  const canEdit = hasPerm(user, resource, "a");
  const canDel = hasPerm(user, resource, "e");
  const [items, setItems] = useState([]);
  const [rels, setRels] = useState({});
  const [form, setForm] = useState({ nome: "" });
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [pwdConfirm, setPwdConfirm] = useState(null); // {message, action}
  // Estado para "Criar conta de app?" (só usado quando enableUserAccount=true)
  const [createUser, setCreateUser] = useState(false);
  const [userStyle, setUserStyle] = useState("lower"); // lower | upper | cap
  const [userSenha, setUserSenha] = useState("");
  const fileRef = useRef(null);
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("asc"); // asc | desc
  const [sortField, setSortField] = useState("nome");
  const [selected, setSelected] = useState(new Set());

  const toggleSelected = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAllSelected = () => setSelected((s) => s.size === filteredItems.length && filteredItems.length > 0 ? new Set() : new Set(filteredItems.map((it) => it.id)));
  const clearSelection = () => setSelected(new Set());

  const filteredItems = useMemo(() => {
    const q = search.toLowerCase().trim();
    const filtered = q ? items.filter((it) => (it.nome || "").toLowerCase().includes(q)) : items;
    const sorted = [...filtered].sort((a, b) => {
      let av = a[sortField]; let bv = b[sortField];
      if (sortField !== "nome" && extraFields.find((f) => f.key === sortField)?.type === "select") {
        av = (rels[sortField] || []).find((r) => r.id === av)?.nome || "";
        bv = (rels[sortField] || []).find((r) => r.id === bv)?.nome || "";
      }
      av = (av || "").toString().toLowerCase(); bv = (bv || "").toString().toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv, "pt-BR") : bv.localeCompare(av, "pt-BR");
    });
    return sorted;
  }, [items, search, sortDir, sortField, rels, extraFields]);

  const load = async () => {
    const r = await api.get(`/${path}`); setItems(r.data);
    const relData = {};
    for (const [k, p] of Object.entries(relations)) {
      const rr = await api.get(`/${p}`); relData[k] = rr.data;
    }
    setRels(relData);
  };
  // `load` é redefinido a cada render (fecha sobre `path`, `relations`) — deps intencionais.
  useEffect(() => {
    load();
    setForm({ nome: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, currentEmpresaId]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("empresa-changed", h);
    return () => window.removeEventListener("empresa-changed", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const submit = async () => {
    if (!form.nome) return toast.error("Nome obrigatório");
    try { await api.post(`/${path}`, form); toast.success("Criado"); setForm({ nome: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const del = async (id) => {
    try { await api.delete(`/${path}/${id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const r = await api.post(`/${path}/bulk-delete`, { ids });
      toast.success(`${r.data.deleted} registro(s) excluído(s)`);
      clearSelection();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro na exclusão em lote"); }
  };
  const openEdit = (it) => { setEditItem(it); setEditForm({ ...it }); };
  const saveEdit = async () => {
    try { await api.put(`/${path}/${editItem.id}`, editForm); toast.success("Atualizado"); setEditItem(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const headers = {};
      // Envia X-Empresa-Id para importar no tenant ativo (fetch direto não passa pelo interceptor do api.js)
      if (currentEmpresaId) headers["X-Empresa-Id"] = currentEmpresaId;
      const r = await fetch(`${API}/${path}/import-csv`, { method: "POST", credentials: "include", headers, body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Erro");
      toast.success(`Importados: ${d.created} · Ignorados: ${d.skipped} · Novas regiões: ${d.new_regioes || 0}`);
      load();
    } catch (err) { toast.error(err.message || "Erro na importação"); }
    e.target.value = "";
  };

  // Escapa valor para CSV: aspas duplicadas, envolve em aspas se contém delimitador/quebra
  const csvCell = (v) => {
    const s = v == null ? "" : String(v);
    return /[",;\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportCsv = () => {
    if (!items.length) return toast.error("Nada para exportar");
    // Cabeçalho: nome + labels dos extraFields (relations exportam nome do relacionado, não o id)
    const cols = ["nome", ...extraFields.map((f) => f.type === "select" ? f.label.toLowerCase() : f.key)];
    const header = cols.join(",");
    const rows = items.map((it) => {
      const line = [it.nome];
      for (const f of extraFields) {
        if (f.type === "select") {
          const rel = (rels[f.key] || []).find((r) => r.id === it[f.key]);
          line.push(rel?.nome || "");
        } else {
          line.push(it[f.key] || "");
        }
      }
      return line.map(csvCell).join(",");
    });
    const csv = "\ufeff" + [header, ...rows].join("\r\n"); // BOM para Excel abrir UTF-8 corretamente
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `${path}-${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${items.length} ${path} exportados`);
  };

  return (
    <div className="space-y-6" data-testid={`page-${path}`}>
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-extrabold">{title}</h1>
        <div className="flex gap-2">
          {allowImport && (
            <Button variant="outline" onClick={exportCsv} data-testid={`btn-export-csv-${path}`} title="Baixa CSV para importar em outra empresa">
              <Download className="w-4 h-4 mr-2" />Exportar CSV
            </Button>
          )}
          {allowImport && (
            <div>
              <input ref={fileRef} type="file" accept=".csv" hidden onChange={importCsv} data-testid="input-csv" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="btn-import-csv">
                <Upload className="w-4 h-4 mr-2" />Importar CSV
              </Button>
            </div>
          )}
        </div>
      </div>
      {allowImport && (
        <div className="text-xs text-muted-foreground bg-orange-50 border border-orange-200 rounded p-3">
          <b>Formato CSV:</b> colunas <code>nome</code> (ou <code>bairro</code>) e opcional <code>regiao</code>. Delimitador , ou ;. Regiões novas são criadas automaticamente.
          {" "}<b>Dica:</b> use <b>Exportar CSV</b> aqui, mude para outra empresa no seletor e clique em <b>Importar CSV</b> — os bairros e regiões são recriados no novo tenant.
        </div>
      )}
      <Card>
        <CardHeader><CardTitle>Novo</CardTitle></CardHeader>
        <CardContent className="flex gap-3 flex-wrap items-end">
          <div className="flex-1 min-w-48"><Input data-testid={`input-nome-${path}`} placeholder="Nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
          {extraFields.map((f) => (
            f.type === "select" ? (
              <div key={f.key} className="min-w-40"><Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                <SelectTrigger><SelectValue placeholder={f.label} /></SelectTrigger>
                <SelectContent>{(rels[f.key] || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}</SelectContent>
              </Select></div>
            ) : (
              <Input key={f.key} placeholder={f.label} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
            )
          ))}
          <Button data-testid={`btn-add-${path}`} onClick={submit} className="bg-orange-600 hover:bg-orange-700"><Plus className="w-4 h-4 mr-2" />Adicionar</Button>
        </CardContent>
        {enableUserAccount && (
          <CardContent className="border-t pt-3 space-y-2 bg-orange-50/40">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={createUser} onChange={(e) => setCreateUser(e.target.checked)} className="accent-orange-600 w-4 h-4" data-testid="chk-criar-conta-app"/>
              <UserPlus className="w-4 h-4 text-orange-600"/>
              <span className="font-bold">Criar conta de app para este entregador?</span>
            </label>
            {createUser && (
              <div className="grid md:grid-cols-2 gap-3 pl-6">
                <div>
                  <label className="text-xs font-bold text-muted-foreground uppercase">Estilo do usuário</label>
                  <div className="grid grid-cols-3 gap-1 mt-1">
                    {[
                      { k: "lower", label: "roger" },
                      { k: "upper", label: "ROGER" },
                      { k: "cap", label: "Roger" },
                    ].map((s) => (
                      <button key={s.k} type="button" onClick={() => setUserStyle(s.k)}
                        className={`h-8 rounded border-2 text-xs font-bold transition ${userStyle === s.k ? "bg-orange-600 text-white border-orange-700" : "bg-white text-neutral-700 border-neutral-200 hover:border-orange-300"}`}
                        data-testid={`user-style-${s.k}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Prévia: <b className="text-orange-700">{deriveUsername(form.nome, userStyle) || "—"}</b></p>
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground uppercase">Senha inicial</label>
                  <Input type="password" value={userSenha} onChange={(e) => setUserSenha(e.target.value)} placeholder="mín. 4 caracteres" data-testid="input-user-senha"/>
                  <p className="text-xs text-muted-foreground mt-1">O entregador pode alterar depois no app.</p>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
      <Card>
        <CardHeader className="flex flex-row justify-between items-center gap-3 flex-wrap">
          <CardTitle>Cadastros ({filteredItems.length}{search ? ` de ${items.length}` : ""})</CardTitle>
          <div className="flex gap-2 items-center flex-wrap">
            <div className="relative"><Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar" className="pl-8 w-48 h-9" data-testid={`crud-search-${path}`}/>
            </div>
            {extraFields.length > 0 && (
              <Select value={sortField} onValueChange={setSortField}>
                <SelectTrigger className="w-40 h-9" data-testid="crud-sort-field"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nome">Nome</SelectItem>
                  {extraFields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="sm" onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")} data-testid="crud-sort-toggle" className="h-9">
              {sortDir === "asc" ? <><ArrowUpAZ className="w-4 h-4 mr-1"/>A-Z</> : <><ArrowDownZA className="w-4 h-4 mr-1"/>Z-A</>}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table><TableHeader><TableRow>
            <TableHead className="w-10">
              <input type="checkbox" checked={selected.size > 0 && selected.size === filteredItems.length} onChange={toggleAllSelected}
                className="accent-orange-600 w-4 h-4" data-testid={`chk-all-${path}`} title="Selecionar todos" />
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => { setSortField("nome"); setSortDir(sortDir === "asc" ? "desc" : "asc"); }}>Nome {sortField === "nome" && (sortDir === "asc" ? "↑" : "↓")}</TableHead>
            {extraFields.map((f) => <TableHead key={f.key} className="cursor-pointer" onClick={() => { setSortField(f.key); setSortDir(sortDir === "asc" ? "desc" : "asc"); }}>{f.label} {sortField === f.key && (sortDir === "asc" ? "↑" : "↓")}</TableHead>)}<TableHead className="w-24">Ações</TableHead></TableRow></TableHeader>
            <TableBody>{filteredItems.map((it) => (
              <TableRow key={it.id} className={selected.has(it.id) ? "bg-orange-50/60" : ""}>
                <TableCell><input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSelected(it.id)} className="accent-orange-600 w-4 h-4" data-testid={`chk-${it.id}`} /></TableCell>
                <TableCell className="font-medium">{it.nome}</TableCell>
                {extraFields.map((f) => (
                  <TableCell key={f.key}>{f.type === "select" ? (rels[f.key] || []).find((r) => r.id === it[f.key])?.nome || "—" : it[f.key] || "—"}</TableCell>
                ))}
                <TableCell>
                  <div className="flex gap-1">
                    {canEdit && <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`btn-edit-${it.id}`}><Pencil className="w-4 h-4 text-orange-600" /></Button>}
                    {canDel && <Button size="icon" variant="ghost" onClick={() => del(it.id, it.nome)} data-testid={`btn-del-${it.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
                  </div>
                </TableCell>
              </TableRow>))}
              {filteredItems.length === 0 && <TableRow><TableCell colSpan={3 + extraFields.length} className="text-center text-muted-foreground py-8">{search ? "Nenhum resultado para a busca" : "Sem registros"}</TableCell></TableRow>}
            </TableBody></Table>
        </CardContent>
      </Card>

      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Input value={editForm.nome || ""} onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} placeholder="Nome" data-testid="edit-nome" /></div>
            {extraFields.map((f) => (
              f.type === "select" ? (
                <div key={f.key}><Select value={editForm[f.key] || ""} onValueChange={(v) => setEditForm({ ...editForm, [f.key]: v })}>
                  <SelectTrigger><SelectValue placeholder={f.label} /></SelectTrigger>
                  <SelectContent>{(rels[f.key] || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}</SelectContent>
                </Select></div>
              ) : (
                <Input key={f.key} placeholder={f.label} value={editForm[f.key] || ""} onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })} />
              )
            ))}
            <Button className="w-full bg-orange-600 hover:bg-orange-700" onClick={saveEdit} data-testid="btn-save-edit">Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <PasswordConfirm
        open={!!pwdConfirm}
        onClose={() => setPwdConfirm(null)}
        onConfirmed={() => { pwdConfirm?.action?.(); setPwdConfirm(null); }}
        message={pwdConfirm?.message}
        resource={pwdConfirm?.resource}
        action={pwdConfirm?.verifyAction || "e"}
      />

      {/* Floating bulk-delete bar */}
      {selected.size > 0 && canDel && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white shadow-2xl border-2 border-rose-300 rounded-full px-4 py-2 flex items-center gap-3" data-testid="bulk-bar">
          <span className="text-sm font-semibold text-rose-700"><b>{selected.size}</b> selecionado(s)</span>
          <Button size="sm" variant="ghost" onClick={clearSelection} className="text-xs h-7" data-testid="btn-bulk-clear">Cancelar</Button>
          <Button size="sm" className="bg-rose-600 hover:bg-rose-700 h-8"
            onClick={() => setPwdConfirm({ action: bulkDelete, message: `Digite as credenciais de um usuário autorizado a excluir ${selected.size} ${path}:`, resource: path.replace("-","_"), verifyAction: "e" })}
            data-testid="btn-bulk-delete">
            <Trash2 className="w-4 h-4 mr-1" />Excluir Selecionados
          </Button>
        </div>
      )}
    </div>
  );
}
